import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ClipboardCopy, Factory, PackageCheck, Search, Settings2, Truck, X } from 'lucide-react';
import * as api from '@/api/reposicion.api';
import type { Pedido, PiezaPorAgotarse, Tablero } from '@/api/reposicion.api';
import * as tienda from '@/api/tienda.api';
import type { Producto } from '@/api/tienda.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { fecha, numero } from '@/lib/formato-tienda';

/**
 * REPOSICIÓN — el inventario autogestionable.
 *
 * Esteban: «necesitamos un jefe de operación en inventario, viendo lo que sale y lo que entra…
 * que abastezca el inventario o que tire alertas para que uno como humano también lo haga».
 *
 * Esta pantalla es ese jefe en su versión prudente: avisa de lo que se va a agotar, arma el
 * pedido por suplidor con el mensaje ya escrito y pide el «sí». **No le escribe a nadie.** El
 * mensaje se copia y lo manda una persona; la mercancía entra al inventario cuando alguien
 * dice que llegó.
 */

const MOTIVO: Record<PiezaPorAgotarse['motivo'], { texto: string; tono: 'danger' | 'warning' | 'info' }> = {
  agotada: { texto: 'Agotada', tono: 'danger' },
  bajo_minimo: { texto: 'En su mínimo', tono: 'warning' },
  se_agota_pronto: { texto: 'Se agota pronto', tono: 'info' },
};

const ESTADO: Record<Pedido['estado'], { texto: string; tono: 'warning' | 'info' | 'brand' | 'success' | 'default' }> = {
  PROPUESTO: { texto: 'Espera tu sí', tono: 'warning' },
  APROBADO: { texto: 'Hay que pedirlo', tono: 'info' },
  ENCARGADO: { texto: 'Encargado, por llegar', tono: 'brand' },
  RECIBIDO: { texto: 'Recibido', tono: 'success' },
  DESCARTADO: { texto: 'Descartado', tono: 'default' },
};

const CLAVE = ['inventario', 'reposicion'] as const;
const mensajeDe = (e: unknown, defecto: string) => (e instanceof Error ? e.message : defecto);

export function Reposicion() {
  const toast = useToast();
  const qc = useQueryClient();
  const consulta = useQuery({ queryKey: CLAVE, queryFn: api.tablero });
  const [configurar, setConfigurar] = useState<string | 'buscar' | null>(null);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['inventario'] });
    void qc.invalidateQueries({ queryKey: ['productos'] });
    void qc.invalidateQueries({ queryKey: ['produccion'] });
  };

  const preparar = useMutation({
    mutationFn: (piezas?: Array<{ varianteId: string; cantidad: number }>) => api.prepararPedidos(piezas),
    onSuccess: (creados) => {
      toast.success(creados.length === 1 ? `Pedido ${creados[0]!.numero} preparado. Espera tu sí.` : `${creados.length} pedidos preparados. Esperan tu sí.`);
      refrescar();
    },
    onError: (e) => toast.error(mensajeDe(e, 'No se pudo preparar el pedido')),
  });

  if (consulta.isLoading) return <p className="py-10 text-center text-sm text-slate-500">Mirando el inventario…</p>;
  if (consulta.isError || !consulta.data) {
    return (
      <div className="rounded-2xl border border-surface-border bg-surface p-6 text-center">
        <p className="text-sm text-slate-600 dark:text-slate-300">No se pudo cargar la reposición.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => void consulta.refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }

  const t = consulta.data;
  const porPedir = t.porAgotarse.filter((p) => p.sugerido > 0);
  const abiertos = t.pedidos.filter((p) => p.estado === 'PROPUESTO' || p.estado === 'APROBADO' || p.estado === 'ENCARGADO');
  const cerrados = t.pedidos.filter((p) => p.estado === 'RECIBIDO' || p.estado === 'DESCARTADO');

  return (
    <div data-reposicion className="space-y-6">
      <p className="text-sm text-slate-600 dark:text-slate-300">
        El sistema mira lo que sale con cada venta y lo que entra, y avisa de lo que se va a agotar. Prepara el pedido y
        su mensaje; <strong>no le escribe a nadie</strong>: tú dices que sí y una persona lo hace.
      </p>

      {/* ── Lo que se va a agotar ─────────────────────────────────────────── */}
      <section className="rounded-2xl border border-surface-border bg-surface p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Se va a agotar <span className="text-slate-400">({t.porAgotarse.length})</span>
            </h2>
            <p className="text-xs text-slate-500">
              Lo agotado, lo que llegó a su mínimo y lo que al ritmo de venta de los últimos {t.diasDeVenta} días no
              aguanta una semana.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfigurar('buscar')}>
              <Settings2 className="h-4 w-4" /> Mínimos y suplidores
            </Button>
            <Button
              size="sm"
              disabled={porPedir.length === 0 || preparar.isPending}
              loading={preparar.isPending}
              onClick={() => preparar.mutate(undefined)}
              title={porPedir.length === 0 ? 'No hay nada sin pedir' : undefined}
            >
              <PackageCheck className="h-4 w-4" /> Preparar pedidos ({porPedir.length})
            </Button>
          </div>
        </div>

        {t.porAgotarse.length === 0 ? (
          <p className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
            Nada por agotarse. El aviso sale solo cuando una pieza llega a su mínimo o se está vendiendo más rápido de lo
            que queda: ponle su mínimo a cada producto en «Mínimos y suplidores».
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-surface-border dark:divide-slate-800">
            {t.porAgotarse.map((p) => (
              <FilaPorAgotarse
                key={p.varianteId}
                pieza={p}
                tablero={t}
                ocupado={preparar.isPending}
                onConfigurar={() => setConfigurar(p.productoId)}
                onPedir={(cantidad) => preparar.mutate([{ varianteId: p.varianteId, cantidad }])}
              />
            ))}
          </ul>
        )}
      </section>

      {/* ── Los pedidos ───────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          Pedidos en curso <span className="text-slate-400">({abiertos.length})</span>
        </h2>
        {abiertos.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-surface-border p-4 text-sm text-slate-500">
            No hay pedidos en curso. Cuando prepares uno queda aquí, esperando tu sí.
          </p>
        ) : (
          abiertos.map((p) => <TarjetaDePedido key={p.id} pedido={p} tablero={t} onCambio={refrescar} />)
        )}
        {cerrados.length > 0 && (
          <details className="rounded-2xl border border-surface-border bg-surface p-4">
            <summary className="cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-200">
              Últimos pedidos cerrados ({cerrados.length})
            </summary>
            <ul className="mt-3 space-y-2 text-sm">
              {cerrados.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-medium tabular-nums">{p.numero}</span>
                  <Badge variant={ESTADO[p.estado].tono}>{ESTADO[p.estado].texto}</Badge>
                  <span className="text-slate-600 dark:text-slate-300">{deQuien(p)}</span>
                  <span className="text-slate-500">
                    {p.lineas.map((l) => `${p.estado === 'RECIBIDO' ? (l.recibida ?? 0) : l.cantidad} × ${l.producto} (${l.talla})`).join(' · ')}
                  </span>
                  <span className="text-xs text-slate-400">{fecha(p.recibidoAt ?? p.createdAt)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {configurar && (
        <ConfigurarProducto
          productoId={configurar === 'buscar' ? null : configurar}
          tablero={t}
          onCerrar={() => setConfigurar(null)}
          onGuardado={refrescar}
        />
      )}
    </div>
  );
}

const deQuien = (p: Pedido) => (p.fabricacion ? 'Taller (se fabrica)' : (p.suplidor?.nombre ?? 'Sin suplidor'));

/** Dónde está la pieza, en los negocios con almacén y tiendas. */
function dondeEsta(tablero: Tablero, varianteId: string): string | null {
  if (!tablero.lugares) return null;
  const v = tablero.lugares.porVariante[varianteId];
  return tablero.lugares.lugares
    .map((l) => `${l.nombre}: ${l.tipo === 'ALMACEN' ? (v?.enAlmacen ?? 0) : (v?.porTienda[l.id] ?? 0)}`)
    .join(' · ');
}

function FilaPorAgotarse({
  pieza: p,
  tablero,
  ocupado,
  onConfigurar,
  onPedir,
}: {
  pieza: PiezaPorAgotarse;
  tablero: Tablero;
  ocupado: boolean;
  onConfigurar: () => void;
  onPedir: (cantidad: number) => void;
}) {
  const donde = dondeEsta(tablero, p.varianteId);
  return (
    <li data-pieza={p.varianteId} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
      <div className="min-w-[12rem] flex-1">
        <p className="font-medium text-slate-900 dark:text-slate-100">
          {p.producto} <span className="text-slate-500">· {p.talla}</span>
        </p>
        <p className="text-xs text-slate-500">
          {p.codigo ? `${p.codigo} · ` : ''}
          {p.suplidores.length > 0
            ? `Se trae de ${p.suplidores.map((s) => s.nombre).join(', ')}`
            : tablero.seFabrica
              ? 'Sin suplidor: se manda a fabricar'
              : 'Sin suplidor asignado'}
        </p>
        {donde && <p className="text-xs text-slate-500">{donde}</p>}
      </div>
      <Badge variant={MOTIVO[p.motivo].tono}>{MOTIVO[p.motivo].texto}</Badge>
      <dl className="flex gap-4 text-center text-xs text-slate-500">
        <div>
          <dt>Quedan</dt>
          <dd className="text-base font-semibold tabular-nums text-slate-900 dark:text-slate-100">{numero(p.quedan)}</dd>
        </div>
        <div>
          <dt>Mínimo</dt>
          <dd className="text-base tabular-nums text-slate-700 dark:text-slate-200">{p.minimo > 0 ? numero(p.minimo) : '—'}</dd>
        </div>
        <div>
          <dt>Vendidas</dt>
          <dd className="text-base tabular-nums text-slate-700 dark:text-slate-200">{numero(p.vendidas)}</dd>
        </div>
        <div>
          <dt>Aguanta</dt>
          <dd className="text-base tabular-nums text-slate-700 dark:text-slate-200">{p.dias == null ? '—' : `${p.dias} d`}</dd>
        </div>
      </dl>
      <div className="flex items-center gap-2">
        {p.sugerido > 0 ? (
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => onPedir(p.sugerido)}>
            Pedir {numero(p.sugerido)}
          </Button>
        ) : (
          <span className="text-xs text-slate-500">Ya van {numero(p.enPedido)} en un pedido</span>
        )}
        <Button variant="ghost" size="icon" onClick={onConfigurar} title="Mínimo y suplidor" aria-label={`Mínimo y suplidor de ${p.producto}`}>
          <Settings2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function Copiable({ titulo, texto }: { titulo: string; texto: string }) {
  const toast = useToast();
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success('Mensaje copiado. Pégalo donde lo vayas a mandar.');
    } catch {
      toast.error('Este navegador no dejó copiar: selecciona el texto y cópialo a mano.');
    }
  };
  return (
    <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/50">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300">{titulo}</p>
        <Button variant="ghost" size="sm" onClick={() => void copiar()}>
          <ClipboardCopy className="h-3.5 w-3.5" /> Copiar
        </Button>
      </div>
      <pre className="mt-1 select-all whitespace-pre-wrap break-words font-sans text-sm text-slate-800 dark:text-slate-100">{texto}</pre>
    </div>
  );
}

function TarjetaDePedido({ pedido: p, tablero, onCambio }: { pedido: Pedido; tablero: Tablero; onCambio: () => void }) {
  const toast = useToast();
  const [quienBusca, setQuienBusca] = useState(p.quienBusca ?? '');
  const [recibiendo, setRecibiendo] = useState(false);

  const hacer = useMutation({
    mutationFn: (que: () => Promise<Pedido>) => que(),
    onSuccess: onCambio,
    onError: (e) => {
      toast.error(mensajeDe(e, 'No se pudo cambiar el pedido'));
      onCambio();
    },
  });
  const ocupado = hacer.isPending;
  const sinDestino = !p.fabricacion && !p.suplidor;

  return (
    <article data-pedido={p.numero} className="rounded-2xl border border-surface-border bg-surface p-4 sm:p-5">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{p.numero}</h3>
        <Badge variant={ESTADO[p.estado].tono}>{ESTADO[p.estado].texto}</Badge>
        <span className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
          {p.fabricacion ? <Factory className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
          {deQuien(p)}
          {p.suplidor?.telefono ? ` · ${p.suplidor.telefono}` : ''}
        </span>
        <span className="ml-auto text-xs text-slate-400">{fecha(p.createdAt)}</span>
      </header>

      <ul className="mt-3 space-y-1.5">
        {p.lineas.map((l) => (
          <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-slate-800 dark:text-slate-100">
              {l.producto} <span className="text-slate-500">· {l.talla} · quedan {numero(l.quedan)}</span>
            </span>
            {p.estado === 'PROPUESTO' ? (
              <span className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min={1}
                  className="h-9 w-20 text-right"
                  aria-label={`Cantidad de ${l.producto} ${l.talla}`}
                  defaultValue={l.cantidad}
                  disabled={ocupado}
                  onBlur={(e) => {
                    const n = Math.floor(Number(e.target.value));
                    if (!Number.isFinite(n) || n < 1) {
                      e.target.value = String(l.cantidad);
                      return;
                    }
                    if (n !== l.cantidad) hacer.mutate(() => api.editarPedido(p.id, { lineas: [{ id: l.id, cantidad: n }] }));
                  }}
                />
                {p.lineas.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={ocupado}
                    title="Quitar del pedido"
                    aria-label={`Quitar ${l.producto} ${l.talla} del pedido`}
                    onClick={() => hacer.mutate(() => api.editarPedido(p.id, { lineas: [{ id: l.id, cantidad: 0 }] }))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </span>
            ) : (
              <span className="font-medium tabular-nums">× {numero(l.cantidad)}</span>
            )}
          </li>
        ))}
      </ul>

      {p.estado === 'PROPUESTO' && sinDestino && (
        <div className="mt-3">
          <label className="mb-1 block text-xs text-slate-500">¿A quién se le pide?</label>
          {tablero.suplidores.length === 0 ? (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Todavía no hay suplidores. Créalos en «Mínimos y suplidores».
            </p>
          ) : (
            <Select
              value=""
              aria-label="¿A quién se le pide?"
              disabled={ocupado}
              onChange={(e) => {
                // Se lee AHORA: cuando la petición sale, React ya devolvió el selector a «Elige…».
                const suplidorId = e.target.value;
                if (suplidorId) hacer.mutate(() => api.editarPedido(p.id, { suplidorId }));
              }}
            >
              <option value="">Elige el suplidor…</option>
              {tablero.suplidores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </Select>
          )}
        </div>
      )}

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <Copiable titulo={p.fabricacion ? 'Para el taller' : 'Para el suplidor'} texto={p.mensajes.paraElSuplidor} />
        {p.mensajes.paraQuienBusca && p.estado !== 'PROPUESTO' && (
          <Copiable titulo="Para quien lo va a buscar" texto={p.mensajes.paraQuienBusca} />
        )}
      </div>

      <footer className="mt-4 flex flex-wrap items-end gap-2">
        {p.estado === 'PROPUESTO' && (
          <>
            {!p.fabricacion && (
              <label className="block min-w-[12rem] flex-1">
                <span className="mb-1 block text-xs text-slate-500">¿Quién lo va a buscar? (si ya lo sabes)</span>
                <Input value={quienBusca} onChange={(e) => setQuienBusca(e.target.value)} placeholder="el mensajero, tú…" />
              </label>
            )}
            <Button disabled={ocupado} onClick={() => hacer.mutate(() => api.responderPedido(p.id, true, quienBusca.trim()))}>
              <Check className="h-4 w-4" /> {p.fabricacion ? 'Sí, que se fabrique' : 'Sí, hacer el pedido'}
            </Button>
          </>
        )}
        {p.estado === 'APROBADO' && (
          <>
            {!p.fabricacion && (
              <label className="block min-w-[12rem] flex-1">
                <span className="mb-1 block text-xs text-slate-500">Quién lo va a buscar</span>
                <Input
                  value={quienBusca}
                  onChange={(e) => setQuienBusca(e.target.value)}
                  onBlur={() => {
                    if (quienBusca.trim() !== (p.quienBusca ?? '')) hacer.mutate(() => api.editarPedido(p.id, { quienBusca: quienBusca.trim() || null }));
                  }}
                  placeholder="el mensajero, tú…"
                />
              </label>
            )}
            <Button disabled={ocupado} onClick={() => hacer.mutate(() => api.marcarEncargado(p.id))}>
              <Check className="h-4 w-4" /> {p.fabricacion ? 'Ya está en el taller' : 'Ya se le pidió'}
            </Button>
          </>
        )}
        {(p.estado === 'APROBADO' || p.estado === 'ENCARGADO') && (
          <Button variant={p.estado === 'ENCARGADO' ? 'primary' : 'outline'} disabled={ocupado} onClick={() => setRecibiendo(true)}>
            <PackageCheck className="h-4 w-4" /> Llegó
          </Button>
        )}
        <Button variant="ghost" disabled={ocupado} onClick={() => hacer.mutate(() => api.responderPedido(p.id, false))}>
          {p.estado === 'PROPUESTO' ? 'No, descartar' : 'Descartar'}
        </Button>
      </footer>

      {recibiendo && (
        <RecibirPedido
          pedido={p}
          onCerrar={() => setRecibiendo(false)}
          onRecibido={() => {
            setRecibiendo(false);
            onCambio();
          }}
        />
      )}
    </article>
  );
}

function RecibirPedido({ pedido: p, onCerrar, onRecibido }: { pedido: Pedido; onCerrar: () => void; onRecibido: () => void }) {
  const toast = useToast();
  const [llego, setLlego] = useState<Record<string, string>>(() => Object.fromEntries(p.lineas.map((l) => [l.id, String(l.cantidad)])));
  const cantidades = p.lineas.map((l) => ({ id: l.id, cantidad: Math.floor(Number(llego[l.id] ?? 0)) }));
  const pedida = new Map(p.lineas.map((l) => [l.id, l.cantidad]));
  const deMas = cantidades.some((c) => c.cantidad > (pedida.get(c.id) ?? 0));
  const valido = !deMas && cantidades.every((c) => Number.isFinite(c.cantidad) && c.cantidad >= 0) && cantidades.some((c) => c.cantidad > 0);

  const recibir = useMutation({
    mutationFn: () => api.recibirPedido(p.id, cantidades),
    onSuccess: () => {
      toast.success(`Pedido ${p.numero} recibido: ya entró al inventario.`);
      onRecibido();
    },
    onError: (e) => toast.error(mensajeDe(e, 'No se pudo recibir el pedido')),
  });

  return (
    <Modal open onClose={onCerrar} title={`Llegó el pedido ${p.numero}`} description="Escribe lo que de verdad llegó. Eso es lo que entra al inventario.">
      <div className="space-y-3">
        {p.lineas.map((l) => (
          <label key={l.id} className="flex items-center justify-between gap-3 text-sm">
            <span>
              {l.producto} <span className="text-slate-500">· {l.talla} · se pidieron {numero(l.cantidad)}</span>
            </span>
            <Input
              type="number"
              min={0}
              max={l.cantidad}
              className="h-10 w-24 text-right"
              value={llego[l.id] ?? ''}
              onChange={(e) => setLlego((prev) => ({ ...prev, [l.id]: e.target.value }))}
            />
          </label>
        ))}
        {deMas && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            No puede llegar más de lo que se pidió. Si llegó de más, dale entrada aquí a lo pedido y lo demás regístralo
            aparte, en Existencias → Mover existencia.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => recibir.mutate()} disabled={!valido || recibir.isPending} loading={recibir.isPending}>
            Dar entrada
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * De quién se trae un producto y cuál es el mínimo de cada talla. Sin producto elegido, primero
 * se busca.
 */
function ConfigurarProducto({
  productoId,
  tablero,
  onCerrar,
  onGuardado,
}: {
  productoId: string | null;
  tablero: Tablero;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const [buscar, setBuscar] = useState('');
  const [elegido, setElegido] = useState<Producto | null>(null);
  const productos = useQuery({
    queryKey: ['productos', 'reposicion', productoId ?? buscar],
    queryFn: () => tienda.listarProductos({ buscar: productoId ? undefined : buscar, limit: productoId ? 200 : 20 }),
  });
  const producto = elegido ?? (productoId ? (productos.data ?? []).find((x) => x.id === productoId) ?? null : null);

  return (
    <Modal
      open
      onClose={onCerrar}
      size="lg"
      title={producto ? producto.name : 'Mínimos y suplidores'}
      description={producto ? 'De quién se trae y con cuántas piezas avisa cada talla.' : 'Busca el producto que quieres dejar listo.'}
    >
      {producto ? (
        <FormularioDeProducto producto={producto} tablero={tablero} onCerrar={onCerrar} onGuardado={onGuardado} />
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input autoFocus className="pl-9" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Nombre o código del producto…" />
          </div>
          {productos.isLoading ? (
            <p className="text-sm text-slate-500">Buscando…</p>
          ) : (productos.data ?? []).length === 0 ? (
            <p className="text-sm text-slate-500">
              {buscar.trim() ? 'Ningún producto con ese nombre.' : 'Todavía no hay productos cargados. Se cargan en Productos.'}
            </p>
          ) : (
            <ul className="max-h-72 divide-y divide-surface-border overflow-y-auto dark:divide-slate-800">
              {(productos.data ?? []).map((x) => (
                <li key={x.id}>
                  <button type="button" className="flex w-full items-center justify-between gap-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50" onClick={() => setElegido(x)}>
                    <span>
                      {x.name} {x.sku && <span className="text-slate-500">· {x.sku}</span>}
                    </span>
                    <span className="text-xs text-slate-500">{(tablero.suplidoresPorProducto[x.id] ?? []).length} suplidor(es)</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}

function FormularioDeProducto({
  producto,
  tablero,
  onCerrar,
  onGuardado,
}: {
  producto: Producto;
  tablero: Tablero;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [suplidores, setSuplidores] = useState(tablero.suplidores);
  const [marcados, setMarcados] = useState<string[]>(tablero.suplidoresPorProducto[producto.id] ?? []);
  const [minimos, setMinimos] = useState<Record<string, string>>(() =>
    Object.fromEntries(producto.variants.map((v) => [v.id, String(v.lowStockThreshold)])),
  );
  const [nuevo, setNuevo] = useState({ nombre: '', telefono: '' });

  const crearSuplidor = useMutation({
    mutationFn: () => tienda.crearSuplidor({ nombre: nuevo.nombre.trim(), telefono: nuevo.telefono.trim() || null }),
    onSuccess: (s) => {
      setSuplidores((prev) => [...prev, { id: s.id, nombre: s.nombre, telefono: s.telefono }]);
      setMarcados((prev) => [...prev, s.id]);
      setNuevo({ nombre: '', telefono: '' });
      void qc.invalidateQueries({ queryKey: CLAVE });
    },
    onError: (e) => toast.error(mensajeDe(e, 'No se pudo crear el suplidor')),
  });

  const numeros = producto.variants.map((v) => ({ varianteId: v.id, minimo: Math.floor(Number(minimos[v.id] ?? 0)) }));
  const valido = numeros.every((m) => Number.isFinite(m.minimo) && m.minimo >= 0);

  const guardar = useMutation({
    mutationFn: () => api.configurarProducto(producto.id, { suplidorIds: marcados, minimos: numeros }),
    onSuccess: () => {
      toast.success('Guardado. El aviso ya cuenta con esto.');
      onGuardado();
      onCerrar();
    },
    onError: (e) => toast.error(mensajeDe(e, 'No se pudo guardar')),
  });

  return (
    <div className="space-y-5">
      <fieldset>
        <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">¿De quién se trae?</legend>
        {suplidores.length === 0 ? (
          <p className="mt-1 text-sm text-slate-500">
            Todavía no hay suplidores. {tablero.seFabrica ? 'Si lo fabricas tú, déjalo sin suplidor.' : 'Crea el primero aquí abajo.'}
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {suplidores.map((s) => {
              const dentro = marcados.includes(s.id);
              return (
                <label
                  key={s.id}
                  className={`flex min-h-[40px] cursor-pointer items-center gap-2 rounded-full border px-3 text-sm ${dentro ? 'border-brand-500 bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300' : 'border-surface-border text-slate-700 dark:border-slate-700 dark:text-slate-200'}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={dentro}
                    onChange={() => setMarcados((prev) => (dentro ? prev.filter((x) => x !== s.id) : [...prev, s.id]))}
                  />
                  {s.nombre}
                </label>
              );
            })}
          </div>
        )}
        <div className="mt-3 grid gap-2 sm:grid-cols-[2fr_1fr_auto]">
          <Input value={nuevo.nombre} onChange={(e) => setNuevo((n) => ({ ...n, nombre: e.target.value }))} placeholder="Nuevo suplidor: nombre" aria-label="Nombre del suplidor nuevo" />
          <Input value={nuevo.telefono} onChange={(e) => setNuevo((n) => ({ ...n, telefono: e.target.value }))} placeholder="Teléfono" aria-label="Teléfono del suplidor nuevo" />
          <Button variant="outline" disabled={!nuevo.nombre.trim() || crearSuplidor.isPending} onClick={() => crearSuplidor.mutate()}>
            Añadir
          </Button>
        </div>
      </fieldset>

      <fieldset>
        <legend className="text-sm font-medium text-slate-800 dark:text-slate-100">Mínimo de cada talla</legend>
        <p className="text-xs text-slate-500">Cuando queden esas o menos, avisa. Con 0 solo avisa si se está vendiendo y no aguanta una semana.</p>
        {producto.variants.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Este producto todavía no tiene tallas.</p>
        ) : (
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {producto.variants.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-3 rounded-xl border border-surface-border px-3 py-2 text-sm dark:border-slate-800">
                <span>
                  {v.label} <span className="text-slate-500">· hay {numero(v.stock)}</span>
                </span>
                <Input
                  type="number"
                  min={0}
                  className="h-9 w-20 text-right"
                  aria-label={`Mínimo de la talla ${v.label}`}
                  value={minimos[v.id] ?? ''}
                  onChange={(e) => setMinimos((prev) => ({ ...prev, [v.id]: e.target.value }))}
                />
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button onClick={() => guardar.mutate()} disabled={!valido || guardar.isPending} loading={guardar.isPending}>
          Guardar
        </Button>
      </div>
    </div>
  );
}
